import React, { useEffect, useRef, useState } from 'react';
import { education } from '../data/mock';
import { MapPin, Award, BookOpen, GraduationCap } from 'lucide-react';
import { Badge } from './ui/badge';

const Education = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="education"
      ref={sectionRef}
      className="py-14 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 right-10 w-32 h-32 border border-gray-300/40 dark:border-gray-700/30 rounded-full animate-float opacity-30" />
        <div className="absolute bottom-40 left-20 w-24 h-24 border border-gray-300/40 dark:border-gray-700/30 rotate-45 animate-float-delayed opacity-30" />
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-gradient-to-br from-gray-200/40 via-gray-100/20 to-transparent dark:from-gray-700/30 dark:via-gray-800/15 rounded-full blur-3xl animate-morph opacity-40" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse opacity-30" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="mb-16">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white mb-4">
              Education
            </h2>
          </div>
          <p className={`text-gray-600 dark:text-gray-400 text-lg font-light transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Academic foundation in Computer Science and Engineering
          </p>
          {/* Animated underline */}
          <div
            className={`h-1 bg-gradient-to-r from-black dark:from-white to-transparent mt-6 transition-all duration-1000 delay-200 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'
              }`}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {education.map((edu, index) => (
            <div
              key={edu.institution}
              className={`group bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 p-8 rounded-2xl border border-gray-800 dark:border-gray-800 transition-all duration-700 hover:border-gray-700 dark:hover:border-gray-600 hover:-translate-y-2 hover:shadow-2xl opacity-60 hover:opacity-70 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95'
                }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Header with icon */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-gray-700 group-hover:bg-white rounded-xl flex items-center justify-center transition-all duration-500">
                  <GraduationCap size={28} className="text-gray-300 group-hover:text-black transition-colors duration-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-medium text-white mb-1">{edu.institution}</h3>
                  <div className="flex items-center gap-1 text-sm text-gray-400">
                    <MapPin size={14} />
                    <span>{edu.location}</span>
                  </div>
                </div>
              </div>

              {/* Degree */}
              <div className="p-4 rounded-lg border border-gray-700 dark:border-gray-700 bg-gray-800/50 dark:bg-gray-800/30 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={16} className="text-gray-400 dark:text-gray-500" />
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">Degree</p>
                </div>
                <p className="text-base text-gray-300 dark:text-gray-300 font-light">{edu.degree}</p>
              </div>

              {/* GPA and Period */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-gray-800/50 dark:bg-gray-800/60 px-4 py-2 rounded-full border border-gray-700 dark:border-gray-700">
                  <Award size={14} className="text-gray-400 dark:text-gray-400" />
                  <span className="text-sm text-gray-300 dark:text-gray-400">
                    GPA: <span className="text-gray-200 dark:text-white font-medium">{edu.gpa}</span>
                  </span>
                </div>
                {edu.period && (
                  <span className="text-sm text-gray-300 dark:text-gray-500 bg-gray-800/50 dark:bg-gray-800/40 px-3 py-2 rounded-full border border-gray-700 dark:border-gray-700">{edu.period}</span>
                )}
              </div>

              {/* Coursework */}
              <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/30">
                <p className="text-xs text-gray-600 dark:text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2 font-medium">
                  <span className="w-4 h-px bg-gray-400 dark:bg-gray-600" />
                  Relevant Coursework
                </p>
                <div className="flex flex-wrap gap-2">
                  {edu.coursework.map((course) => (
                    <Badge
                      key={course}
                      variant="outline"
                      className="text-xs font-light border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-400 hover:border-gray-600 dark:hover:border-gray-400 hover:text-black dark:hover:text-white transition-all duration-300"
                    >
                      {course}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Education;
